/**
 * # WithTooltip
 *
 * Helper to render a tooltip around another component.
 *
 * ## Props
 *
 * - `anchorFixedPositionProps` (object): Props to be passed to the AnchoredFixedPosition component.
 * - `as` (string): The HTML tag or React component to be used as the wrapper element. Default is 'div'.
 * - `behavior` (string): The behavior of the tooltip. Possible values are 'hover' and 'click'. Default is 'hover'.
 * - `children` (node): The content to be wrapped by the tooltip.
 * - `className` (string): Additional CSS class name(s) to be applied to the wrapper element.
 * - `content` (node): The content of the tooltip.
 * - `display` (string): The display property of the wrapper element. Possible values are 'block', 'flex', and 'inline-block'. Default is 'inline-block'.
 * - `enabled` (boolean): Whether the tooltip is enabled or not. Default is true.
 * - `focusable` (boolean): Whether the tooltip can receive focus or not. Default is false.
 * - `hideFocus` (boolean): Whether to hide the focus outline or not. Default is false.
 * - `placement` (string): The placement of the tooltip. Possible values are 'top', 'right', 'bottom', and 'left'. Default is 'top'.
 * - `propagateHoverEvent` (boolean): Whether to propagate hover events to the wrapper element or not. Default is false.
 * - `wrapperProps` (object): Props to be passed to the wrapper element.
 * - `zLevel` (string): The z-index level of the tooltip. Default is 'tooltip'.
 * // bottom two props (showClose, allowOnlyOneTooltip) are most likely used with behavior='click' if necessary. With behavior='hover', these two are not needed because the tooltip will close when the mouse leaves the target.
 * - `showClose` (boolean): Whether to show a close button in the tooltip. Default is false.
 * - `allowOnlyOneTooltip` (boolean): Whether to allow only one tooltip to be open at a time Default is false.
 * - `showDelay` (number): The delay in milliseconds before showing the tooltip. Default is 0.
 * - `tooltipClassName` (string): Additional CSS class name(s) to be applied to the tooltip element.
 * - `showArrow` (boolean): Whether to show an arrow pointing to the target. Default is false.
 *
 * ## Usage
 *
 * ```tsx
 * <WithTooltip
 *   enabled={buttonIsDisabled}
 *   content='Sorry, the button is disabled for some reason'
 * >
 *   <Button disabled={buttonIsDisabled}>
 *     Click Me
 *   </Button>
 * </WithTooltip>
 * ```
 */

/**
 * # Tooltip
 *
 * Tooltip component
 *
 * ## Props
 *
 * - `as` (string|func): The HTML tag or React component to be used as the wrapper element. Default is 'div'.
 * - `children` (node): The content of the tooltip.
 * - `className` (string): Additional CSS class name(s) to be applied to the wrapper element.
 * - `placement` (string): The placement of the tooltip. Possible values are 'top', 'right', 'bottom', and 'left'. Default is 'top'.
 * - `theme` (string): The theme of the tooltip. Possible values are 'dark' and 'light'. Default is 'dark'.
 *
 * ## Usage
 *
 * ```tsx
 * <Tooltip
 *   content='This is a tooltip'
 *   placement='bottom'
 *   theme='light'
 * >
 *   <Button>
 *     Hover Me
 *   </Button>
 * </Tooltip>
 * ```
 */
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState
} from 'react';
import classnames from 'classnames';
import makeConstants from 'lib/makeConstants';
import {handleTap} from 'lib/touchUtils';
// Import the atoms directly rather than from the '@albert-io/atomic' barrel:
// the barrel pulls in this Tooltip (via IconButton → MenuListItem), so importing
// it back creates a circular dependency that throws a TDZ error at load time
// ("Cannot access '_Bannerreact' before initialization").
import Text from '../../atoms/Text/Text.react';
import Anchor from '../../atoms/Anchor/Anchor.react';

import type {TextColor} from '../../atoms/Text/Text.react';
import AnchoredFixedPosition from '../../helpers/AnchoredFixedPosition/AnchoredFixedPosition.react';

import './tooltip.scss';

// Unique event name to close all tooltips when one is opened (if allowOnlyOneTooltip is true)
const TOOLTIP_CLOSE_ALL_EVENT = 'tooltipcloseallevent';
let TOOLTIP_UID = 0;

// Google-style grouped delay: while tooltips are "warm" (one was shown or hidden
// within GROUP_GRACE_WINDOW ms), the next tooltip skips its showDelay and appears
// instantly. Shared at module scope so every tooltip coordinates without a wrapper.
const GROUP_GRACE_WINDOW = 1500;
let lastTooltipActivityAt = 0;

export const placements = makeConstants(
  'auto-start',
  'auto',
  'auto-end',
  'top-start',
  'top',
  'top-end',
  'right-start',
  'right',
  'right-end',
  'bottom-end',
  'bottom',
  'bottom-start',
  'left-end',
  'left',
  'left-start'
);
export const themes = makeConstants('dark', 'light');
export const behaviors = makeConstants('hover', 'click');

export type Placement = ValueOf<typeof placements>;
export type Theme = ValueOf<typeof themes>;
export type Behavior = ValueOf<typeof behaviors>;

export interface WithTooltipProps extends PropsWithChildrenOptional, PropsWithClassNameOptional {
  anchoredFixedPositionProps?: Partial<React.ComponentProps<typeof AnchoredFixedPosition>>;
  as?: React.ElementType;
  behavior?: Behavior;
  content?: React.ReactNode;
  display?: 'block' | 'flex' | 'inline-block';
  enabled?: boolean;
  focusable?: boolean;
  hideFocus?: boolean;
  placement?: Placement;
  propagateHoverEvent?: boolean;
  wrapperProps?: Record<string, unknown>;
  zLevel?: string;
  showClose?: boolean;
  allowOnlyOneTooltip?: boolean;
  showDelay?: number;
  tooltipClassName?: string;
  showArrow?: boolean;
  theme?: Theme;
  /**
   * Inline style forwarded (via `...rest`) to the inner `Tooltip`/`Text`, e.g.
   * to constrain the tooltip width with `maxWidth`.
   */
  style?: React.CSSProperties;
}

/**
 * Imperative API exposed via `ref` (e.g. `tooltipRef.current?.deactivate()`).
 */
export interface WithTooltipHandle {
  activate: () => void;
  deactivate: () => void;
  toggle: () => void;
}

// Stable default so an omitted `wrapperProps` doesn't create a new object (and a
// new spread) on every render.
const DEFAULT_WRAPPER_PROPS: Record<string, unknown> = {};

/**
 * Helper to render a tooltip around another component.
 *
 * @example
 * <WithTooltip
 *   enabled={buttonIsDisabled}
 *   content='Sorry, the button is disabled for some reason'
 * >
 *   <Button disabled={buttonIsDisabled}>
 *     Click Me
 *   </Button>
 * </WithTooltip>
 */
export const WithTooltip = forwardRef<WithTooltipHandle, WithTooltipProps>(
  (
    {
      as: Wrapper = 'div',
      anchoredFixedPositionProps,
      behavior = behaviors.hover,
      children,
      className,
      content,
      display = 'inline-block',
      enabled = true,
      focusable = false,
      hideFocus,
      placement = placements.top,
      propagateHoverEvent,
      showArrow = false,
      showClose = false,
      showDelay = 0,
      allowOnlyOneTooltip = false,
      theme = themes.dark,
      tooltipClassName,
      wrapperProps = DEFAULT_WRAPPER_PROPS,
      zLevel = 'tooltip',
      // `behavior` and `showDelay` are destructured above so they aren't forwarded
      // down to the visual `Tooltip` (and onto the DOM) as stray attributes.
      ...rest
    },
    ref
  ) => {
    const [isActive, setIsActive] = useState(false);

    // Stable per-instance id used to coordinate the `allowOnlyOneTooltip` behavior.
    const [instanceId] = useState(() => ++TOOLTIP_UID);

    const showTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const wrapperRef = useRef<HTMLElement>(null);
    const tooltipWrapperRef = useRef<HTMLDivElement>(null);

    // Mirror `isActive` into a ref so the stable callbacks below (global listeners,
    // `deactivate`) can read the latest value without being re-created on change.
    // The ref is never read during render, so writing it here is safe.
    const isActiveRef = useRef(isActive);
    isActiveRef.current = isActive;

    const activate = useCallback(() => {
      if (showTimeoutRef.current) {
        clearTimeout(showTimeoutRef.current);
      }

      // If another tooltip was recently active, skip the delay so consecutive
      // hovers feel instant (Google-style grouped behavior).
      const isWarm = Date.now() - lastTooltipActivityAt < GROUP_GRACE_WINDOW;
      const delay = isWarm ? 0 : showDelay;

      showTimeoutRef.current = setTimeout(() => {
        // If `allowOnlyOneTooltip` is true, dispatch a global event to close all other tooltips
        if (allowOnlyOneTooltip) {
          window.dispatchEvent(new CustomEvent(TOOLTIP_CLOSE_ALL_EVENT, {detail: instanceId}));
        }
        lastTooltipActivityAt = Date.now();
        setIsActive(true);
      }, delay);
    }, [showDelay, allowOnlyOneTooltip, instanceId]);

    const deactivate = useCallback(() => {
      if (showTimeoutRef.current) {
        clearTimeout(showTimeoutRef.current);
      }
      // Keep the group "warm" from the moment a visible tooltip is dismissed, so
      // moving to a nearby trigger shows its tooltip without re-waiting the delay.
      if (isActiveRef.current) {
        lastTooltipActivityAt = Date.now();
      }
      setIsActive(false);
    }, []);

    const toggle = useCallback(() => setIsActive((prev) => !prev), []);

    // Expose the imperative API consumers rely on (e.g. `ref.current.deactivate()`).
    useImperativeHandle(ref, () => ({activate, deactivate, toggle}), [
      activate,
      deactivate,
      toggle
    ]);

    // Clear any pending show timer on unmount.
    useEffect(
      () => () => {
        if (showTimeoutRef.current) {
          clearTimeout(showTimeoutRef.current);
        }
      },
      []
    );

    const handleClickOutsideComponent = useCallback(
      (e: MouseEvent) => {
        if (!isActiveRef.current) {
          return;
        }
        const target = e.target as Node;

        const clickedOutside =
          (!wrapperRef.current || !wrapperRef.current.contains(target)) &&
          (!tooltipWrapperRef.current || !tooltipWrapperRef.current.contains(target));

        if (clickedOutside) {
          deactivate();
        }
      },
      [deactivate]
    );

    const handleGlobalClose = useCallback(
      (e: Event) => {
        // close all the other tooltips except this one. used for allowOnlyOneTooltip prop
        if ((e as CustomEvent<number>).detail !== instanceId && isActiveRef.current) {
          deactivate();
        }
      },
      [deactivate, instanceId]
    );

    // https://github.com/albert-io/nexus/issues/12944
    // If `allowOnlyOneTooltip` is true, then only one tooltip can be open at a time.
    // Refer to the ticket #12944 for reference.
    useEffect(() => {
      if (!allowOnlyOneTooltip) {
        return undefined;
      }
      global.document.addEventListener('mousedown', handleClickOutsideComponent);
      global.document.addEventListener(TOOLTIP_CLOSE_ALL_EVENT, handleGlobalClose);
      return () => {
        global.document.removeEventListener('mousedown', handleClickOutsideComponent);
        global.document.removeEventListener(TOOLTIP_CLOSE_ALL_EVENT, handleGlobalClose);
      };
    }, [allowOnlyOneTooltip, handleClickOutsideComponent, handleGlobalClose]);

    const handleClick = useCallback(() => {
      if (behavior === behaviors.click) {
        toggle();
      }
    }, [behavior, toggle]);

    const handleTouchStart = useCallback(
      (e: React.TouchEvent) => {
        if (behavior === behaviors.click) {
          return;
        }
        handleTap(e.target, toggle);
      },
      [behavior, toggle]
    );

    const handleMouseEnter = useCallback(() => {
      if (behavior === behaviors.hover) {
        activate();
      }
    }, [behavior, activate]);

    const handleMouseLeave = useCallback(() => {
      if (behavior === behaviors.hover) {
        deactivate();
      }
    }, [behavior, deactivate]);

    const handleMouseOver = useCallback(() => {
      if (behavior === behaviors.hover) {
        activate();
      }
    }, [behavior, activate]);

    const handleMouseOut = useCallback(() => {
      if (behavior === behaviors.hover) {
        deactivate();
      }
    }, [behavior, deactivate]);

    const handleBlur = useCallback(() => {
      if (focusable) {
        deactivate();
      }
    }, [focusable, deactivate]);

    const handleFocus = useCallback(() => {
      if (focusable) {
        activate();
      }
    }, [focusable, activate]);

    const handleClose = useCallback(() => {
      // if `showClose` is true, close button will be available in the tooltip to close it
      if (showClose) {
        deactivate();
      }
    }, [showClose, deactivate]);

    const hoverEventHandlers = propagateHoverEvent
      ? {
          onMouseOut: handleMouseOut,
          onMouseOver: handleMouseOver
        }
      : {
          onMouseEnter: handleMouseEnter,
          onMouseLeave: handleMouseLeave
        };

    const style: React.CSSProperties = {
      display,
      ...(hideFocus ? {outline: 'none'} : {})
    };

    return (
      <Wrapper
        ref={wrapperRef}
        style={style}
        onClick={handleClick}
        onBlur={handleBlur}
        onFocus={handleFocus}
        onTouchStart={handleTouchStart}
        tabIndex={focusable ? 0 : null}
        {...hoverEventHandlers}
        {...wrapperProps}
      >
        {children}
        {enabled && isActive && (
          <AnchoredFixedPosition
            className={classnames(className, `app-portal--z-${zLevel}`)}
            target={wrapperRef.current || undefined}
            placement={placement}
            arrowClassName={
              showArrow
                ? classnames('a-tooltip-caret', `a-tooltip-caret--theme-${theme}`)
                : undefined
            }
            {...anchoredFixedPositionProps}
            modifiers={{
              offset: {offset: showArrow ? '0, 10' : '0, 8'},
              ...(anchoredFixedPositionProps && anchoredFixedPositionProps.modifiers)
            }}
          >
            <div
              ref={tooltipWrapperRef}
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <Tooltip
                className={tooltipClassName}
                showClose={showClose}
                theme={theme}
                onClose={handleClose}
                {...rest}
              >
                {content}
              </Tooltip>
            </div>
          </AnchoredFixedPosition>
        )}
      </Wrapper>
    );
  }
);

WithTooltip.displayName = 'WithTooltip';

/**
 * Tooltip component
 */

export interface TooltipProps extends PropsWithChildrenOptional, PropsWithClassNameOptional {
  as?: React.ElementType;
  theme?: Theme;
  placement?: Placement | null;
  showClose?: boolean;
  onClose?: () => void;
}

const themeToPropMap = {
  [themes.dark]: {color: 'primary-inverse'},
  [themes.light]: {color: 'secondary'}
} as const satisfies Record<Theme, {color: TextColor}>;

export const Tooltip = ({
  as = 'div',
  children,
  className,
  theme = themes.dark,
  placement = placements.top,
  showClose,
  onClose,
  ...rest
}: TooltipProps) => (
  <Text
    {...rest}
    as={as}
    className={classnames(
      className,
      'a-tooltip',
      `a-tooltip--theme-${theme}`,
      `a-tooltip--placement-${placement}`
    )}
    color={themeToPropMap[theme].color}
    size='s'
  >
    {children}
    {showClose && (
      <div className='flex justify-end gap-x-x0-half'>
        <Anchor standalone inheritColor onClick={onClose}>
          Close
        </Anchor>
      </div>
    )}
  </Text>
);
